import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Select } from "@/components/ui/input";
import { api, q } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  type ContentJob,
  type LocationContent,
  type OrgType,
  type ProspectDTO,
  contentCounts,
  contentIsEmpty,
  resolveOrgType,
} from "@mm-sales/shared";
import { useQuery } from "@tanstack/react-query";
import { Building2, Globe, Loader2, RefreshCw, Sparkles, Wand2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * "What the demo account is filled with": org type (organization vs franchisor
 * dashboard) and the events / memberships / classes / services read from the
 * prospect's site and the web, or generated as samples. One shared set, plus a
 * location's own set when its page lists its own schedule. Everything here is
 * saved on the prospect and applied when a demo is built.
 */
export function DemoContentCard({
  prospect,
  onChange,
  autoStart = false,
  embedded = false,
}: {
  prospect: ProspectDTO;
  onChange: (p: ProspectDTO) => void;
  /** Read from their site as soon as the card mounts, when nothing has been read yet. */
  autoStart?: boolean;
  /** Inside the wizard: no outer Card chrome. */
  embedded?: boolean;
}) {
  const [err, setErr] = useState<string | null>(null);
  const job = prospect.contentJob;
  const running = job?.status === "running";

  // Poll while a run is in progress; hand the fresh prospect up.
  const poll = useQuery({
    queryKey: ["prospect-content", prospect.id],
    queryFn: () => q.prospect(prospect.id),
    enabled: running,
    refetchInterval: running ? 1500 : false,
  });
  useEffect(() => {
    if (poll.data && JSON.stringify(poll.data.contentJob) !== JSON.stringify(prospect.contentJob))
      onChange(poll.data);
  }, [poll.data, prospect.contentJob, onChange]);

  const start = async (mode: "discover" | "sample", scope = "all") => {
    setErr(null);
    try {
      const r = await api.post<{ job: ContentJob }>(
        `/v1/prospects/${prospect.id}/content/discover`,
        {
          scope,
          mode,
        },
      );
      onChange({ ...prospect, contentJob: r.job });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  const started = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once per prospect, on mount
  useEffect(() => {
    if (!autoStart || started.current) return;
    if (contentIsEmpty(prospect.content) && !prospect.contentJob) {
      started.current = true;
      void start(prospect.website ? "discover" : "sample");
    }
  }, [autoStart, prospect.id]);

  const setOrgType = async (orgType: OrgType) => {
    setErr(null);
    try {
      onChange(await api.patch<ProspectDTO>(`/v1/prospects/${prospect.id}`, { orgType }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  const dropLocationSet = async (locationId: string) => {
    setErr(null);
    try {
      onChange(
        await api.put<ProspectDTO>(`/v1/prospects/${prospect.id}/content`, {
          locationId,
          content: null,
        }),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const locCount = prospect.locations.length || prospect.locationsCount || 1;
  const effective = resolveOrgType(prospect.orgType, locCount);
  const ownSets = prospect.locations.filter((l) => !contentIsEmpty(l.content));

  const body = (
    <div className="space-y-4">
      <Field label="Set up in MyManager as">
        <div className="flex items-center gap-3">
          <Select
            value={prospect.orgType ?? "auto"}
            onChange={(e) => setOrgType(e.target.value as OrgType)}
            className="max-w-xs"
          >
            <option value="auto">
              Automatic ({effective === "franchisor" ? "franchisor" : "organization"})
            </option>
            <option value="organization">Organization — one account</option>
            <option value="franchisor">Franchisor — org dashboard, locations, fees, reports</option>
          </Select>
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Building2 className="h-3.5 w-3.5" />
            {locCount} location{locCount === 1 ? "" : "s"}
          </span>
        </div>
      </Field>

      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-sm font-medium">Events, memberships, classes &amp; services</span>
          <span className="text-xs text-muted-foreground">
            {originLabel(prospect.content)}
            {prospect.content?.generatedAt
              ? ` · ${new Date(prospect.content.generatedAt).toLocaleString()}`
              : ""}
          </span>
        </div>
        <CountRow content={prospect.content} />
        {ownSets.length ? (
          <div className="mt-2 divide-y rounded-md border text-xs">
            {ownSets.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                <span className="truncate">
                  <span className="font-medium">{l.name}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    · own set ({originLabel(l.content)})
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <Counts content={l.content} />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dropLocationSet(l.id)}
                    title="Use the shared set for this location instead"
                  >
                    use shared
                  </Button>
                </span>
              </div>
            ))}
          </div>
        ) : null}
        <p className="mt-1.5 text-xs text-muted-foreground">
          Read from their website and the web, then topped up with samples for their kind of
          business so nothing is empty. Every location gets the shared set unless its own page lists
          its own schedule. Up to 100 of each.
        </p>
      </div>

      {running ? (
        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="truncate">{job?.message || "Working"}</span>
            <span className="ml-auto text-xs text-muted-foreground">{job?.progress ?? 0}%</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded bg-muted">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${job?.progress ?? 0}%` }}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={contentIsEmpty(prospect.content) ? "default" : "outline"}
            size="sm"
            onClick={() => start("discover")}
            disabled={!prospect.website && !prospect.locations.some((l) => l.pageUrl || l.website)}
          >
            <Globe className="h-3.5 w-3.5" />{" "}
            {contentIsEmpty(prospect.content) ? "Read from their site" : "Read again"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => start("sample")}>
            <Wand2 className="h-3.5 w-3.5" /> Use samples only
          </Button>
          {prospect.locations.length >= 2 ? (
            <span className="self-center text-xs text-muted-foreground">
              <RefreshCw className="mr-1 inline h-3 w-3" />
              reads{" "}
              {
                prospect.locations.filter(
                  (l) => l.pageUrl || (l.website && l.website !== prospect.website),
                ).length
              }{" "}
              location pages too
            </span>
          ) : null}
        </div>
      )}
      {job?.status === "failed" ? (
        <p className="text-xs text-destructive">
          Reading failed: {job.error || "unknown error"}. Samples are used until you try again.
        </p>
      ) : null}
      {err ? <p className="text-xs text-destructive">{err}</p> : null}
    </div>
  );

  if (embedded) return body;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" /> What the demo account is filled with
        </CardTitle>
        <CardDescription>
          The org type and the events, memberships, classes and services the demo is seeded with.
          Saved on the prospect; applied when a demo is built.
        </CardDescription>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}

const originLabel = (c: LocationContent | null | undefined) =>
  !c || contentIsEmpty(c)
    ? "nothing yet — samples at build"
    : c.origin === "site"
      ? "from their site"
      : c.origin === "search"
        ? "from the web"
        : c.origin === "mixed"
          ? "their site + samples"
          : c.origin === "manual"
            ? "edited"
            : "samples";

function Counts({ content }: { content: LocationContent | null | undefined }) {
  const n = contentCounts(content);
  return (
    <span className="tabular-nums text-muted-foreground">
      {n.events}e · {n.memberships}m · {n.classes}c · {n.services}s
    </span>
  );
}

function CountRow({ content }: { content: LocationContent | null | undefined }) {
  const n = contentCounts(content);
  const empty = contentIsEmpty(content);
  const cell = (label: string, v: number) => (
    <div className={cn("rounded-md p-2 text-center", empty ? "bg-muted/50" : "bg-muted")}>
      <div
        className={cn("text-lg font-semibold tabular-nums", empty ? "text-muted-foreground" : "")}
      >
        {v}
      </div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
  return (
    <div className="grid grid-cols-4 gap-2">
      {cell("events", n.events)}
      {cell("memberships", n.memberships)}
      {cell("classes", n.classes)}
      {cell("services", n.services)}
    </div>
  );
}
